import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useFocusEffect, useIsFocused } from '@react-navigation/core';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { isEmpty } from 'lodash';
import { useAppTranslation } from '../../../hooks/useAppTranslation';
import Layout from '../../../components/Layout';
import {
  AppNavigationProp,
  NavigationParamList,
  ScreenName,
  StackNavigationScreenProps,
} from '../../../navigation/types';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import {
  AdminHelpdeskIssueRead,
  HelpdeskIssueStatus,
  Organization,
  PersonaType,
  Role,
  HelpdeskIssueTranslationRole,
  Topic,
} from '../../../client/interfaces';
import {
  Button,
  FormInput,
  FormErrorNotification,
  Modal,
  PersonaCheckboxGroup,
  EditorInput,
  DropdownWithLink,
} from '../../../components';
import { useAdmin } from '../../../contexts/Admin/AdminContext';
import { useAdminHelpdesk } from '../../../contexts/Admin/AdminHelpdeskContext';
import { useMessage } from '../../../contexts/Messages/MessageContext';
import client from '../../../client/client';
import { isWeb } from '../../../utils/responsive';
import { getRichTextLength } from '../../../utils/richTextLength';
import { Language } from '../../../resources/interfaces';

type HelpdeskIssueSchemaTranslations = {
  [key in Language]: { [key in HelpdeskIssueTranslationRole]: string };
};

/* eslint-disable camelcase */
interface HelpdeskIssueSchema {
  translations: HelpdeskIssueSchemaTranslations;
  topic: Topic;
  organization: Organization;
  personaKeys: PersonaType[];
  published: boolean;
}

const emptyFormValues: HelpdeskIssueSchema = {
  translations: {
    hu: {
      title: '',
      description: '',
      cta_link: '',
    },
    en: {
      title: '',
      description: '',
      cta_link: '',
    },
  },
  topic: undefined,
  organization: undefined,
  personaKeys: [],
  published: false,
};

const FormikDirtyObserver: React.FC<{ setIsDirty: (v: boolean) => void }> = ({ setIsDirty }) => {
  const { dirty } = useFormikContext();
  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty]);
  return null;
};

const AdminHelpdeskIssueEditScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.AdminHelpdeskIssueEditScreen>
> = ({ navigation: nav, route }) => {
  const navigation = nav as AppNavigationProp;
  const { t } = useAppTranslation();
  const { me, hasRole } = useAuth();
  const { isBackModalOpen, setIsBackModalOpen, setIsDirty } = useAdmin();
  const { topics, organizations, refreshTopicsAndOrganizations } = useAdminHelpdesk();
  const isFocused = useIsFocused();

  const [initialFormValues, setInitialFormValues] = useState<HelpdeskIssueSchema>(emptyFormValues);
  const { setMessage } = useMessage();

  const resetFormRef = useRef<((nextState?: { values: HelpdeskIssueSchema }) => void) | null>(null);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const currentPostIdRef = useRef<number | undefined>(route.params?.postId);
  const [inactiveModalType, setInactiveModalType] = useState<
    'topic' | 'organization' | 'both' | null
  >(null);
  const pendingPublishActionRef = useRef<(() => Promise<void>) | null>(null);

  // Topic search state
  const [topicSearch, setTopicSearch] = useState('');

  // Organization search state
  const [orgSearch, setOrgSearch] = useState('');

  useFocusEffect(
    useCallback(() => {
      setIsBackModalOpen(false);
      setIsDirty(false);
    }, []),
  );

  useEffect(() => {
    refreshTopicsAndOrganizations();
  }, []);

  const navigateAfterAction = () => {
    const target = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (target) {
      target();
    } else {
      navigation.navigate(ScreenName.AdminHelpdeskIssueListScreen);
    }
  };

  useEffect(() => {
    currentPostIdRef.current = route.params?.postId;
  }, [route.params?.postId]);

  const filteredTopics = useMemo(() => {
    if (!topicSearch) return topics;
    return topics.filter((topic) =>
      topic.translationValue?.toLowerCase().includes(topicSearch.toLowerCase()),
    );
  }, [topics, topicSearch]);

  const filteredOrganizations = useMemo(() => {
    if (!orgSearch) return organizations;
    return organizations.filter((org) => org.name?.toLowerCase().includes(orgSearch.toLowerCase()));
  }, [organizations, orgSearch]);

  const getInactiveType = (
    topicId: number | undefined,
    orgId: number | undefined,
  ): 'topic' | 'organization' | 'both' | null => {
    const topicInactive = topics.find((tp) => tp.id === topicId)?.isActive === false;
    const orgInactive = organizations.find((o) => o.id === orgId)?.isActive === false;
    if (topicInactive && orgInactive) return 'both';
    if (topicInactive) return 'topic';
    if (orgInactive) return 'organization';
    return null;
  };

  const isNewHelpdeskIssue = !route?.params?.postId;
  const isArchivedIssue = route?.params?.initialStatus === HelpdeskIssueStatus.archived;
  const isPublishedIssue = !isNewHelpdeskIssue && initialFormValues.published;
  const requiredTranslation = t('admin:common:required_field');
  const tooLongTranslation = (max) => t('admin:common:max_characters', { max });
  const descriptionValidation = Yup.string()
    .required(requiredTranslation)
    .test('description-visible-max', tooLongTranslation(500), (value) => {
      const length = getRichTextLength(value);
      return length <= 500;
    });

  const helpdeskIssueDraftSchema = Yup.object().shape({
    translations: Yup.object().shape({
      hu: Yup.object().shape({
        title: Yup.string().required(requiredTranslation).max(100, tooLongTranslation(100)),
        description: descriptionValidation,
        cta_link: Yup.string().url(t('error_messages:url_format')),
      }),
      en: Yup.object().shape({
        title: Yup.string().required(requiredTranslation).max(100, tooLongTranslation(100)),
        description: descriptionValidation,
        cta_link: Yup.string().url(t('error_messages:url_format')),
      }),
    }),
  });

  const helpdeskIssueSchema = Yup.object().shape({
    translations: Yup.object().shape({
      hu: Yup.object().shape({
        title: Yup.string().required(requiredTranslation).max(100, tooLongTranslation(100)),
        description: descriptionValidation,
        cta_link: Yup.string().url(t('error_messages:url_format')),
      }),
      en: Yup.object().shape({
        title: Yup.string().required(requiredTranslation).max(100, tooLongTranslation(100)),
        description: descriptionValidation,
        cta_link: Yup.string().url(t('error_messages:url_format')),
      }),
    }),
    topic: Yup.object().required(requiredTranslation),
    organization: Yup.object().required(requiredTranslation),
    personaKeys: Yup.array().min(1, requiredTranslation),
    published: Yup.boolean(),
  });

  const transformHelpdeskIssueToSchema = (
    helpdeskIssue: AdminHelpdeskIssueRead,
    freshTopics?: Topic[],
    freshOrganizations?: Organization[],
  ): HelpdeskIssueSchema => {
    const getTranslation = (lang: Language, role: HelpdeskIssueTranslationRole) =>
      helpdeskIssue.allTranslations?.find((t) => t.language === lang && t.role === role)?.value ||
      '';

    const topicList = freshTopics ?? topics;
    const orgList = freshOrganizations ?? organizations;
    const topic = topicList.find((tp) => tp.id === helpdeskIssue.helpdeskIssue?.topicId);
    const organization = orgList.find(
      (org) => org.id === helpdeskIssue.helpdeskIssue?.organizationId,
    );

    return {
      translations: {
        hu: {
          title: getTranslation(Language.hu, HelpdeskIssueTranslationRole.title),
          description: getTranslation(Language.hu, HelpdeskIssueTranslationRole.description),
          cta_link: getTranslation(Language.hu, HelpdeskIssueTranslationRole.ctaLink),
        },
        en: {
          title: getTranslation(Language.en, HelpdeskIssueTranslationRole.title),
          description: getTranslation(Language.en, HelpdeskIssueTranslationRole.description),
          cta_link: getTranslation(Language.en, HelpdeskIssueTranslationRole.ctaLink),
        },
      },
      topic,
      organization,
      personaKeys: helpdeskIssue.personas?.map((persona) => persona.key) || [],
      published: !!helpdeskIssue.publishedAtUtc,
    };
  };

  const transformSchemaToHelpdeskIssue = (values: HelpdeskIssueSchema) => {
    const { translations, topic, organization, personaKeys, published } = values;

    // Transform translations to API format
    const translationsArray = Object.entries(translations).flatMap(([lang, trans]) => {
      return Object.entries(trans)
        .map(([key, value]) => ({
          language: lang as Language,
          value,
          role: key as HelpdeskIssueTranslationRole,
        }))
        .filter((t) => !!t.value);
    });

    return {
      translations: translationsArray,
      helpdeskTopicId: topic?.id,
      helpdeskOrganizationId: organization?.id,
      personaKeys,
      published,
    };
  };

  const saveChanges = async (values: HelpdeskIssueSchema) => {
    try {
      const helpdeskIssue = transformSchemaToHelpdeskIssue({ ...values, published: true });
      await client.updateAdminHelpdeskIssue(route.params.postId, helpdeskIssue);
      resetFormRef.current?.({ values: emptyFormValues });
      navigateAfterAction();
    } catch (e) {
      setMessage({ message: e.message, type: 'error' });
    }
  };

  const saveDraft = async (
    values: HelpdeskIssueSchema,
    setFieldTouched: (field: string, isTouched: boolean) => void,
    setFieldError: (field: string, message: string) => void,
  ) => {
    try {
      await helpdeskIssueDraftSchema.validate(values, { abortEarly: false });
    } catch (e) {
      if (e instanceof Yup.ValidationError) {
        e.inner.forEach((err) => {
          setFieldTouched(err.path, true);
          setFieldError(err.path, err.message);
        });
        if (isWeb) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
    }
    try {
      const helpdeskIssue = transformSchemaToHelpdeskIssue({ ...values, published: false });
      if (isNewHelpdeskIssue) {
        const { id: newPostId } = await client.createAdminHelpdeskIssues(helpdeskIssue);
        currentPostIdRef.current = newPostId;
      } else {
        await client.updateAdminHelpdeskIssue(route.params.postId, helpdeskIssue);
      }
      resetFormRef.current?.({ values: emptyFormValues });
      navigateAfterAction();
    } catch (e) {
      setMessage({ message: e.message, type: 'error' });
    }
  };

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const helpdeskIssue = transformSchemaToHelpdeskIssue(values);
      if (isNewHelpdeskIssue) {
        await client.createAdminHelpdeskIssues(helpdeskIssue);
      } else {
        await client.updateAdminHelpdeskIssue(route.params.postId, helpdeskIssue);
      }
      setSubmitting(false);
      resetForm({ values: emptyFormValues });
      navigateAfterAction();
    } catch (e) {
      setMessage({ message: e.message, type: 'error' });
    }
  };

  // Role guard
  useFocusEffect(
    useCallback(() => {
      if (me && !hasRole(Role.helpdeskIssueAdmin)) {
        navigation.navigate(ScreenName.HomeScreen);
      }
    }, [me?.roleList]),
  );

  useFocusEffect(
    useCallback(() => {
      if (isWeb) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }

      const fetchHelpdeskIssue = async () => {
        try {
          const { topics: freshTopics, organizations: freshOrganizations } =
            await refreshTopicsAndOrganizations();
          const helpdeskIssueRes = await client.getAdminHelpdeskIssue(route.params.postId);
          const transformed = transformHelpdeskIssueToSchema(
            helpdeskIssueRes,
            freshTopics,
            freshOrganizations,
          );
          setInitialFormValues(transformed);
        } catch (e) {
          setMessage({ message: e.message, type: 'error' });
          navigation.navigate(ScreenName.AdminHelpdeskIssueListScreen);
        }
      };

      if (route?.params?.postId) {
        fetchHelpdeskIssue();
      } else {
        refreshTopicsAndOrganizations();
        setInitialFormValues(emptyFormValues);
      }
    }, [route?.params?.postId]),
  );

  return (
    <Layout
      title={
        isNewHelpdeskIssue
          ? t('admin:helpdesk_issue_edit:create_title')
          : t('admin:helpdesk_issue_edit:edit_title')
      }
    >
      <Formik
        initialValues={initialFormValues}
        validationSchema={helpdeskIssueSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          handleSubmit,
          values,
          setFieldValue,
          setFieldTouched,
          setFieldError,
          isSubmitting,
          resetForm,
          validateForm,
          dirty,
        }) => {
          resetFormRef.current = resetForm;
          const hasInactiveSelection =
            !hasRole(Role.superAdmin) &&
            (values.topic?.isActive === false || values.organization?.isActive === false);
          const canPublishWithInactive =
            hasRole(Role.superAdmin) &&
            !!getInactiveType(values.topic?.id, values.organization?.id);
          return (
            <View className="flex-1">
              <FormikDirtyObserver setIsDirty={setIsDirty} />
              <FormErrorNotification />
              <View className="flex-1 flex-row gap-x-20 mb-10 z-30">
                {/* Hungarian column */}
                <View className="flex-1">
                  <FormInput
                    name="translations.hu.title"
                    title={t('admin:helpdesk_issue_edit:title')}
                    placeholder={t('admin:common:max_characters', { max: 100 })}
                    required
                  />
                  {isWeb && (
                    <EditorInput
                      name="translations.hu.description"
                      title={t('admin:helpdesk_issue_edit:description')}
                      placeholder={t('admin:common:max_characters', { max: 500 })}
                      required
                      helperText={t('admin:helpdesk_issue_edit:description_helper')}
                    />
                  )}
                  <FormInput
                    name="translations.hu.cta_link"
                    title={t('admin:helpdesk_issue_edit:cta_link')}
                  />

                  <DropdownWithLink
                    title={t('admin:helpdesk_issue_edit:topic')}
                    name="topic"
                    inputValue={topicSearch}
                    onChangeText={setTopicSearch}
                    selectedItem={values.topic}
                    filteredContent={filteredTopics}
                    onSelect={(item) => {
                      setFieldValue('topic', item);
                      setTopicSearch('');
                    }}
                    errorMessage={
                      !hasRole(Role.superAdmin) && values.topic?.isActive === false
                        ? t('admin:helpdesk_issue_edit:publish_inactive_error_topic')
                        : undefined
                    }
                    helperText={t('admin:helpdesk_issue_edit:topic_helper_new')}
                    onNavigateToCreate={() => {
                      pendingNavigationRef.current = () =>
                        navigation.navigate(ScreenName.AdminHelpdeskTopicEditScreen, {
                          returnScreen: ScreenName.AdminHelpdeskIssueEditScreen,
                          returnPostId: currentPostIdRef.current,
                        });
                      if (!dirty) {
                        navigateAfterAction();
                      } else {
                        setIsBackModalOpen(true);
                      }
                    }}
                    zIndex={40}
                  />

                  <DropdownWithLink
                    title={t('admin:helpdesk_issue_edit:organization')}
                    name="organization"
                    inputValue={orgSearch}
                    onChangeText={setOrgSearch}
                    selectedItem={values.organization}
                    filteredContent={filteredOrganizations}
                    onSelect={(item) => {
                      setFieldValue('organization', item);
                      setOrgSearch('');
                    }}
                    errorMessage={
                      !hasRole(Role.superAdmin) && values.organization?.isActive === false
                        ? t('admin:helpdesk_issue_edit:publish_inactive_error_org')
                        : undefined
                    }
                    helperText={t('admin:helpdesk_issue_edit:organization_helper_new')}
                    onNavigateToCreate={() => {
                      pendingNavigationRef.current = () =>
                        navigation.navigate(ScreenName.AdminHelpdeskOrganizationEditScreen, {
                          returnScreen: ScreenName.AdminHelpdeskIssueEditScreen,
                          returnPostId: currentPostIdRef.current,
                        });
                      if (!dirty) {
                        navigateAfterAction();
                      } else {
                        setIsBackModalOpen(true);
                      }
                    }}
                    zIndex={30}
                  />

                  <PersonaCheckboxGroup required={true} />
                </View>

                {/* English column */}
                <View className="flex-1">
                  <FormInput
                    name="translations.en.title"
                    title={t('admin:helpdesk_issue_edit:title_en')}
                    placeholder={t('admin:common:max_characters', { max: 100 })}
                    required
                  />
                  {isWeb && (
                    <EditorInput
                      name="translations.en.description"
                      title={t('admin:helpdesk_issue_edit:description_en')}
                      placeholder={t('admin:common:max_characters', { max: 500 })}
                      required
                      helperText={t('admin:helpdesk_issue_edit:description_helper')}
                    />
                  )}
                  <FormInput
                    name="translations.en.cta_link"
                    title={t('admin:helpdesk_issue_edit:cta_link')}
                  />
                </View>
              </View>
              <View className="self-end flex-row gap-x-6">
                <Button
                  text={t('admin:common:exit_from_edit')}
                  size="medium"
                  variant="link"
                  onPress={() => {
                    pendingNavigationRef.current = null;
                    if (!dirty) {
                      navigation.navigate(ScreenName.AdminHelpdeskIssueListScreen);
                    } else {
                      setIsBackModalOpen(true);
                    }
                  }}
                  disabled={isSubmitting}
                />
                {isPublishedIssue ? (
                  <Button
                    text={t('admin:common:save_changes')}
                    size="medium"
                    variant="fill"
                    onPress={async () => {
                      if (hasRole(Role.superAdmin)) {
                        const inactiveType = getInactiveType(
                          values.topic?.id,
                          values.organization?.id,
                        );
                        if (inactiveType) {
                          pendingPublishActionRef.current = async () => handleSubmit();
                          setInactiveModalType(inactiveType);
                          return;
                        }
                      }
                      handleSubmit();
                    }}
                    disabled={
                      isSubmitting ||
                      (!dirty && !canPublishWithInactive && !isArchivedIssue) ||
                      hasInactiveSelection
                    }
                  />
                ) : (
                  <View className="gap-x-6 flex-row">
                    <Button
                      text={t('admin:common:save_as_draft')}
                      size="medium"
                      variant="outline"
                      onPress={() => saveDraft(values, setFieldTouched, setFieldError)}
                      disabled={isSubmitting || !dirty}
                    />
                    <Button
                      text={t('admin:common:publish')}
                      size="medium"
                      variant="fill"
                      onPress={async () => {
                        const validationErrors = await validateForm(values);
                        if (!isEmpty(validationErrors)) {
                          handleSubmit();
                          if (isWeb && validationErrors.translations) {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                          return;
                        }
                        const inactiveType = getInactiveType(
                          values.topic?.id,
                          values.organization?.id,
                        );
                        if (inactiveType) {
                          if (hasRole(Role.superAdmin)) {
                            pendingPublishActionRef.current = async () => {
                              await setFieldValue('published', true);
                              handleSubmit();
                            };
                            setInactiveModalType(inactiveType);
                          } else {
                            setMessage({
                              message: t(
                                `admin:helpdesk_issue_edit:publish_inactive_error_${inactiveType}`,
                              ),
                              type: 'error',
                              translateMessage: false,
                            });
                          }
                          return;
                        }
                        await setFieldValue('published', true);
                        handleSubmit();
                      }}
                      disabled={
                        isSubmitting || (isNewHelpdeskIssue && !dirty) || hasInactiveSelection
                      }
                    />
                  </View>
                )}
              </View>
              <Modal
                showModal={!!inactiveModalType}
                title={
                  inactiveModalType === 'both'
                    ? t('admin:helpdesk_issue_edit:publish_inactive_title_both')
                    : inactiveModalType === 'organization'
                      ? t('admin:helpdesk_issue_edit:publish_inactive_title_org')
                      : t('admin:helpdesk_issue_edit:publish_inactive_title')
                }
                description={
                  inactiveModalType === 'both'
                    ? t('admin:helpdesk_issue_edit:publish_inactive_description_both')
                    : inactiveModalType === 'organization'
                      ? t('admin:helpdesk_issue_edit:publish_inactive_description_org')
                      : t('admin:helpdesk_issue_edit:publish_inactive_description')
                }
                callback={() => {
                  pendingPublishActionRef.current = null;
                  setInactiveModalType(null);
                }}
                closeModal={async () => {
                  await pendingPublishActionRef.current?.();
                  pendingPublishActionRef.current = null;
                  setInactiveModalType(null);
                }}
                yesText={t('admin:helpdesk_issue_edit:publish_inactive_no')}
                cancelText={t('admin:helpdesk_issue_edit:publish_inactive_yes')}
              />
              {isPublishedIssue ? (
                <Modal
                  showModal={isBackModalOpen && isFocused}
                  title={t('admin:helpdesk_issue_edit:save_changes_title')}
                  description={t('admin:helpdesk_issue_edit:save_changes_description')}
                  callback={() => {
                    setIsBackModalOpen(false);
                    resetForm({ values: emptyFormValues });
                    navigateAfterAction();
                  }}
                  closeModal={async () => {
                    setIsBackModalOpen(false);
                    await saveChanges(values);
                  }}
                  yesText={t('admin:helpdesk_issue_edit:save_changes_no')}
                  cancelText={t('admin:helpdesk_issue_edit:save_changes_yes')}
                />
              ) : (
                <Modal
                  showModal={isBackModalOpen && isFocused}
                  title={t('admin:helpdesk_issue_edit:save_as_draft_title')}
                  description={t('admin:helpdesk_issue_edit:save_as_draft_description')}
                  callback={() => {
                    setIsBackModalOpen(false);
                    resetForm({ values: emptyFormValues });
                    navigateAfterAction();
                  }}
                  closeModal={async () => {
                    setIsBackModalOpen(false);
                    await saveDraft(values, setFieldTouched, setFieldError);
                  }}
                  yesText={t('admin:helpdesk_issue_edit:save_as_draft_no')}
                  cancelText={t('admin:helpdesk_issue_edit:save_as_draft_yes')}
                />
              )}
            </View>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default AdminHelpdeskIssueEditScreen;
